/**
 * DashboardChatPanel — reusable multi-turn AI chat for iterating on a dashboard.
 *
 * Sends the full message history + the current widgets each turn to
 * ``POST /api/ai/dashboard-chat`` and calls ``onWidgetsUpdate(newWidgets)`` when the
 * assistant returns a new widget list. Works both:
 *  - inside the Wizard dialog (edits a draft dashboard that doesn't exist yet)
 *  - as a docked panel in Builder (edits a persisted dashboard)
 */
import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Send, Loader2, User, Bot, AlertCircle, CheckCircle2, RefreshCcw, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CHANGE_ICON = { add: "＋", update: "✎", remove: "－", noop: "·" };
const CHANGE_COLOR = { add: "text-[color:var(--success)]", update: "text-[color:var(--brand-ocean)]", remove: "text-[color:var(--danger)]", noop: "text-[color:var(--ink-3)]" };

export function DashboardChatPanel({
  datasetIds = [],
  currentWidgets = [],
  onWidgetsUpdate,
  placeholderPrompts = [],
  className = "",
  compact = false,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [lastMeta, setLastMeta] = useState(null);   // { changes, validations, repaired }
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const canSend = input.trim().length > 0 && !busy && datasetIds.length > 0;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    if (datasetIds.length === 0) { toast.error("Pick at least one dataset first"); return; }
    setError(null);
    const nextHistory = [...messages, { role: "user", content: text }];
    setMessages(nextHistory);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/dashboard-chat", {
        dataset_ids: datasetIds,
        messages: nextHistory,
        current_widgets: currentWidgets,
      });
      setMessages((cur) => [...cur, { role: "assistant", content: data.reply || "Done." }]);
      setLastMeta({ changes: data.changes || [], validations: data.validations || [], repaired: data.repaired || [] });
      if (Array.isArray(data.widgets)) onWidgetsUpdate && onWidgetsUpdate(data.widgets, data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Chat failed");
      setMessages((cur) => [...cur, { role: "assistant", content: `⚠ ${e?.response?.data?.detail || "I couldn't complete that."}`, isError: true }]);
    } finally { setBusy(false); }
  };

  const reset = () => { setMessages([]); setError(null); setLastMeta(null); };

  const badFedCount = (lastMeta?.validations || []).filter((v) => !v.validated).length;

  return (
    <div data-testid="dashboard-chat-panel" className={"flex flex-col h-full min-h-0 " + className}>
      <div className="px-3 py-2 border-b border-[color:var(--glass-border)] flex items-center gap-2 shrink-0">
        <div className="w-7 h-7 rounded-lg hero-gradient flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-[color:var(--brand-ocean)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-[color:var(--ink-2)]">AI dashboard assistant</div>
          <div className="text-[10px] text-[color:var(--ink-3)] truncate">
            {datasetIds.length ? `${datasetIds.length} dataset${datasetIds.length > 1 ? "s" : ""} in scope · ${currentWidgets.length} widget${currentWidgets.length === 1 ? "" : "s"}` : "Pick datasets to start"}
          </div>
        </div>
        {messages.length > 0 && (
          <Button data-testid="chat-reset" size="icon" variant="ghost" onClick={reset} title="New conversation" className="h-7 w-7">
            <RefreshCcw className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 && (
          <div className="text-xs text-[color:var(--ink-3)] space-y-2">
            <p>Ask me to build or edit this dashboard in plain English.</p>
            {placeholderPrompts.length > 0 && (
              <div className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-3)]">Try</div>
                {placeholderPrompts.map((p, i) => (
                  <button
                    key={i}
                    data-testid={`chat-suggestion-${i}`}
                    onClick={() => setInput(p)}
                    className="block w-full text-left text-[11px] px-2 py-1.5 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] hover:border-[color:var(--brand-ocean)] hover:text-[color:var(--brand-ocean)] transition-colors"
                  >{p}</button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} data-testid={`chat-msg-${m.role}-${i}`} className={"flex gap-2 " + (m.role === "user" ? "flex-row-reverse" : "")}>
            <div className={"w-6 h-6 rounded-md flex items-center justify-center shrink-0 " + (m.role === "user" ? "bg-[color:var(--brand-ocean)] text-white" : "bg-[color:var(--surface-2)] text-[color:var(--brand-ocean)]")}>
              {m.role === "user" ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
            </div>
            <div className={"max-w-[85%] px-3 py-2 rounded-2xl text-xs whitespace-pre-wrap " + (
              m.role === "user"
                ? "bg-[color:var(--brand-ocean)] text-white"
                : m.isError
                  ? "bg-[color:rgba(232,90,90,0.06)] border border-[color:var(--danger)]/40 text-[color:var(--danger)]"
                  : "bg-[color:var(--surface-2)] text-[color:var(--ink-2)]"
            )}>
              {m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex gap-2 items-center text-[11px] text-[color:var(--ink-3)]">
            <div className="w-6 h-6 rounded-md flex items-center justify-center bg-[color:var(--surface-2)]"><Loader2 className="w-3 h-3 animate-spin text-[color:var(--brand-ocean)]" /></div>
            AI is designing your changes…
          </div>
        )}

        {lastMeta && !busy && (lastMeta.changes || []).length > 0 && (
          <div data-testid="chat-changes" className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-2.5 text-[11px] space-y-1">
            <div className="text-[color:var(--ink-3)] font-medium">Changes applied</div>
            <ul className="space-y-0.5">
              {lastMeta.changes.slice(0, 8).map((c, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className={"font-mono " + (CHANGE_COLOR[c.type] || "text-[color:var(--ink-3)]")}>{CHANGE_ICON[c.type] || "·"}</span>
                  <span className="text-[color:var(--ink-2)]">{c.summary}</span>
                </li>
              ))}
            </ul>
            {(lastMeta.validations || []).length > 0 && (
              badFedCount === 0 ? (
                <div className="flex items-center gap-1 text-[10px] text-[color:var(--success)] pt-1"><CheckCircle2 className="w-3 h-3" />{lastMeta.validations.length} cross-source widget(s) dry-ran cleanly</div>
              ) : (
                <div className="text-[10px] text-[color:var(--danger)] pt-1"><AlertCircle className="w-3 h-3 inline mr-1" />{badFedCount} cross-source widget(s) failed — check the builder</div>
              )
            )}
          </div>
        )}

        {error && !busy && (
          <div className="flex items-center gap-1.5 text-[11px] text-[color:var(--danger)]">
            <AlertCircle className="w-3.5 h-3.5" />{error}
            <button onClick={() => setError(null)} className="ml-auto text-[color:var(--ink-3)]"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-[color:var(--glass-border)] shrink-0">
        <div className="flex gap-2">
          <Textarea
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={compact ? "Ask to edit…" : "Ask me to add a chart, edit a widget, filter data…"}
            className={"flex-1 bg-[color:var(--surface)] text-xs " + (compact ? "h-9 min-h-[36px] resize-none" : "h-16 min-h-[64px]")}
            disabled={busy}
          />
          <Button
            data-testid="chat-send"
            onClick={send}
            disabled={!canSend}
            size={compact ? "sm" : "default"}
            className="bg-[color:var(--brand-ocean)] text-white shrink-0"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </Button>
        </div>
        {!compact && (
          <div className="text-[9px] text-[color:var(--ink-3)] mt-1">Enter to send · Shift+Enter for new line</div>
        )}
      </div>

      {(lastMeta?.repaired || []).length > 0 && !busy && (
        <div className="px-3 py-1.5 border-t border-[color:var(--glass-border)] text-[10px] text-[color:var(--brand-ocean)] shrink-0 flex items-center gap-1">
          <Badge variant="outline" className="text-[9px] py-0 h-4 border-[color:var(--brand-ocean)]/40">auto-repair</Badge>
          Fixed {lastMeta.repaired.length} column reference(s)
        </div>
      )}
    </div>
  );
}
